import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';

const AVATARS = [
  { name: 'Lion', emoji: '🦁', color: 'bg-amber-100' },
  { name: 'Cat', emoji: '🐱', color: 'bg-orange-100' },
  { name: 'Dog', emoji: '🐶', color: 'bg-yellow-100' },
  { name: 'Panda', emoji: '🐼', color: 'bg-slate-100' },
  { name: 'Rabbit', emoji: '🐰', color: 'bg-pink-100' },
  { name: 'Owl', emoji: '🦉', color: 'bg-purple-100' },
];

const OnboardingPage = () => {
  const [step, setStep] = useState(0);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user, api, updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.onboarding_complete) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleComplete = async () => {
    if (!selectedAvatar) {
      toast.error('Pick a buddy first!');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/onboarding/complete', {
        avatar: selectedAvatar.name,
      });
      updateUser(response.data.user);
      toast.success('Awesome! Your profile is ready!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Something went wrong. Try again!');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="text-center animate-pop" data-testid="onboarding-welcome">
            <img 
              src="/brain-gym-logo.png" 
              alt="Brain Gym Jr. Logo" 
              className="w-32 h-32 mx-auto mb-6"
            />
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 font-fredoka mb-4">
              Welcome to Brain Gym Jr.!
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-md mx-auto">
              Get ready for 12 weeks of fun puzzles, patterns, and brain games!
            </p>
            <div className="space-y-4 text-left max-w-md mx-auto mb-8">
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xl">⏱️</span>
                </div>
                <p className="text-lg font-medium">20 minutes of fun every day</p>
              </div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xl">🪙</span>
                </div>
                <p className="text-lg font-medium">Earn Brain Coins for rewards</p>
              </div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xl">🌟</span>
                </div>
                <p className="text-lg font-medium">Level up your brain power!</p>
              </div>
            </div>
            <button
              onClick={() => setStep(1)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="onboarding-next-btn"
            >
              Let's Go!
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        );

      case 1:
        return (
          <div className="text-center animate-pop" data-testid="onboarding-avatar">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 font-fredoka mb-2">
              Pick Your Buddy!
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Choose an animal friend for your adventure
            </p>

            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
              {AVATARS.map((avatar) => (
                <button
                  key={avatar.name}
                  onClick={() => setSelectedAvatar(avatar)}
                  className={`relative p-4 rounded-3xl border-4 transition-all hover-bounce tap-shrink
                    ${selectedAvatar?.name === avatar.name
                      ? 'border-primary bg-primary/10 scale-105'
                      : 'border-transparent bg-slate-50 hover:bg-slate-100'
                    }`}
                  data-testid={`avatar-${avatar.name.toLowerCase()}`}
                >
                  <div className={`w-16 h-16 mx-auto rounded-full ${avatar.color} flex items-center justify-center text-4xl mb-2`}>
                    {avatar.emoji}
                  </div>
                  <p className="font-bold text-slate-700">{avatar.name}</p>
                  {selectedAvatar?.name === avatar.name && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-success rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => setStep(0)}
                className="btn-secondary px-6"
                data-testid="onboarding-back-btn"
              >
                Back
              </button>
              <button
                onClick={handleComplete}
                disabled={!selectedAvatar || loading}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="onboarding-complete-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Start My Journey!
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" data-testid="onboarding-page">
      <div className="w-full max-w-2xl">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {[0, 1].map((s) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-colors ${
                s <= step ? 'bg-primary' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {renderStep()}
      </div>
    </div>
  );
};

export default OnboardingPage;
